import { isIdentifierRecalled, type IdentifierRecord } from '@myplayer/schema';
import { IconCheck, IconRecall } from '@myplayer/ui';
import { type FormEvent, type ReactElement } from 'react';
import { useAuth } from './auth-context.js';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardIcon,
  CardRow,
  CardTitle,
  Input,
  LinearSpinner,
  ModalDialog,
} from './components/ui/primitives.js';
import { type RecallActionOutcome } from './products-recall-helpers.js';
import { formatCapabilityRestrictionNotice } from './rbac-ui.js';

export interface ProductRecallSectionProps {
  readonly selectedRecord: IdentifierRecord;
  readonly effectiveResolverHost?: string | undefined;
  readonly canRecall: boolean;
  readonly showRecallConfirm: boolean;
  readonly setShowRecallConfirm: (show: boolean) => void;
  readonly recallReason: string;
  readonly setRecallReason: (reason: string) => void;
  readonly recallOverrideUrl: string;
  readonly setRecallOverrideUrl: (url: string) => void;
  readonly recallError: string | null;
  readonly setRecallError: (err: string | null) => void;
  readonly recallSubmitting: boolean;
  readonly recallLastResult?: RecallActionOutcome | null | undefined;
  readonly setRecallLastResult?: ((res: RecallActionOutcome | null) => void) | undefined;
  readonly onConfirmSetRecall: (e: FormEvent) => Promise<void>;
  readonly onClearRecall: () => Promise<void>;
}

export function ProductRecallSection({
  selectedRecord,
  canRecall,
  showRecallConfirm,
  setShowRecallConfirm,
  recallReason,
  setRecallReason,
  recallOverrideUrl,
  setRecallOverrideUrl,
  recallError,
  setRecallError,
  recallSubmitting,
  recallLastResult,
  setRecallLastResult,
  onConfirmSetRecall,
  onClearRecall,
}: ProductRecallSectionProps): ReactElement {
  const { session } = useAuth();
  const recalled = isIdentifierRecalled(selectedRecord);
  const matchingResult =
    recallLastResult && recallLastResult.identifierKey === selectedRecord.identifierKey
      ? recallLastResult
      : null;

  return (
    <>
      <Card data-testid="product-recall-card">
        {recallSubmitting && <LinearSpinner />}
        <CardHeader>
          <div className="flex min-w-0 items-center gap-3">
            <CardIcon>
              <IconRecall />
            </CardIcon>
            <div className="min-w-0">
              <CardTitle>Emergency recall</CardTitle>
              <CardDescription>
                Immediately redirect all scans of this product to a safety recall notice
              </CardDescription>
            </div>
          </div>
          {canRecall && (
            <div className="flex shrink-0 items-center gap-2">
              {recalled ? (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  data-testid="clear-recall-button"
                  disabled={recallSubmitting}
                  onClick={() => void onClearRecall()}
                >
                  {recallSubmitting ? 'Clearing...' : 'Clear Recall'}
                </Button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  data-testid="open-recall-button"
                  disabled={recallSubmitting}
                  onClick={() => {
                    setShowRecallConfirm(true);
                    setRecallError(null);
                    setRecallLastResult?.(null);
                  }}
                >
                  <IconRecall />
                  <span>Recall product</span>
                </Button>
              )}
            </div>
          )}
        </CardHeader>

        <CardContent className="space-y-4">
          {!canRecall && (
            <p
              role="status"
              data-testid="recall-permission-notice"
              className="rounded-control border border-subtle bg-surface-muted px-3 py-2 text-[12px] text-fg-secondary"
            >
              {formatCapabilityRestrictionNotice({
                capability: 'recall:activate_deactivate',
                actionSummary: 'activate or clear emergency product recalls',
                callerRole: session?.personaRole,
              })}
            </p>
          )}

          {matchingResult !== null && (
            <div
              role="status"
              data-testid="recall-action-result"
              className={`rounded-card border p-3.5 text-[12px] ${
                matchingResult.action === 'SET_RECALL'
                  ? 'border-status-error-border bg-status-error-bg text-status-error'
                  : 'border-status-success-border bg-status-success-bg text-status-success'
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 font-bold">
                    {matchingResult.action === 'SET_RECALL' ? <IconRecall /> : <IconCheck />}
                    <span>
                      {matchingResult.action === 'SET_RECALL'
                        ? `Recall activated for /${selectedRecord.ai}/${selectedRecord.primaryKey}`
                        : `Recall cleared for /${selectedRecord.ai}/${selectedRecord.primaryKey}`}
                    </span>
                  </div>
                  <p>
                    {matchingResult.action === 'SET_RECALL' ? (
                      matchingResult.overrideUrl && matchingResult.overrideUrl.trim().length > 0 ? (
                        <>
                          Scanners of{' '}
                          <code>
                            /{selectedRecord.ai}/{selectedRecord.primaryKey}
                          </code>{' '}
                          are now redirected to{' '}
                          <code className="font-mono">{matchingResult.overrideUrl}</code>. This
                          change usually takes effect within about 30 seconds, and never more than
                          60 seconds.
                        </>
                      ) : (
                        <>
                          Scanners of{' '}
                          <code>
                            /{selectedRecord.ai}/{selectedRecord.primaryKey}
                          </code>{' '}
                          are now shown the resolver recall notice page (or redirected to the
                          workspace default fallback URL if configured). This change usually takes
                          effect within about 30 seconds, and never more than 60 seconds.
                        </>
                      )
                    ) : (
                      <>
                        Normal product links for{' '}
                        <code>
                          /{selectedRecord.ai}/{selectedRecord.primaryKey}
                        </code>{' '}
                        have been restored. This change usually takes effect within about 30
                        seconds, and never more than 60 seconds.
                      </>
                    )}
                  </p>
                  <p className="font-mono text-[11px] opacity-90">
                    Status: {matchingResult.status} · Version: v{String(matchingResult.version)} ·
                    Time: {matchingResult.timestamp} · Audit ID: {matchingResult.auditId}
                  </p>
                </div>
                {setRecallLastResult !== undefined && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    data-testid="dismiss-recall-result-button"
                    onClick={() => setRecallLastResult(null)}
                  >
                    Dismiss
                  </Button>
                )}
              </div>
            </div>
          )}

          {recalled ? (
            <div
              role="alert"
              data-testid="recalled-state-banner"
              className="rounded-card border-2 border-status-error-border bg-status-error-bg p-4 text-[12px] text-status-error"
            >
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-[14px] font-bold tracking-wide">
                  <IconRecall />
                  <span>Emergency Product Recall Active</span>
                </div>
                <p>
                  <span className="font-semibold">Reason:</span>{' '}
                  {selectedRecord.recallOverride?.reason ?? 'Emergency safety recall'}
                </p>
                <p>
                  <span className="font-semibold">Recall notice page:</span>{' '}
                  <span className="font-mono">
                    {selectedRecord.recallOverride?.overrideUrl &&
                    selectedRecord.recallOverride.overrideUrl.trim().length > 0
                      ? selectedRecord.recallOverride.overrideUrl
                      : 'Resolver recall notice page (no custom override URL set)'}
                  </span>
                </p>
                {selectedRecord.recallOverride?.activatedAt !== undefined && (
                  <p className="text-[11px]">
                    Activated at {selectedRecord.recallOverride.activatedAt} by{' '}
                    <code>{selectedRecord.recallOverride.activatedBy}</code>
                  </p>
                )}
                <p className="pt-1 text-[11px] opacity-90">
                  Clearing this recall restores normal product links for scanners (usually within
                  about 30 seconds, never more than 60 seconds).
                </p>
                {!canRecall && (
                  <p
                    data-testid="clear-recall-permission-notice"
                    className="pt-1 text-[11px] font-medium text-status-error"
                  >
                    {formatCapabilityRestrictionNotice({
                      capability: 'recall:activate_deactivate',
                      actionSummary: 'clear an active product recall',
                      callerRole: session?.personaRole,
                    })}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="divide-y divide-subtle border-y border-subtle">
              <CardRow label="Recall status">
                <span className="text-[12px] font-medium text-fg-secondary">No active recall</span>
              </CardRow>
            </div>
          )}
        </CardContent>
      </Card>

      {showRecallConfirm && canRecall && !recalled && (
        <ModalDialog
          open={true}
          role="alertdialog"
          variant="destructive"
          title="Confirm Emergency Product Recall"
          subtitle={`Activate recall override for /${selectedRecord.ai}/${selectedRecord.primaryKey}`}
          icon={<IconRecall />}
          maxWidthClassName="max-w-2xl"
          submitting={recallSubmitting}
          onClose={() => {
            setShowRecallConfirm(false);
            setRecallError(null);
          }}
        >
          <form
            data-testid="recall-confirmation-form"
            onSubmit={(e) => void onConfirmSetRecall(e)}
            className="space-y-4 text-[12px]"
          >
            <div
              data-testid="recall-confirmation-explanation"
              className="space-y-1.5 rounded-card border border-status-error-border bg-status-error-bg p-3.5 text-fg"
            >
              <p className="font-semibold text-status-error">
                What will happen when you confirm this recall:
              </p>
              <ul className="list-disc space-y-1 pl-4 text-[12px] text-fg-secondary">
                <li>
                  Anyone who scans{' '}
                  <code className="font-mono text-fg">
                    /{selectedRecord.ai}/{selectedRecord.primaryKey}
                  </code>{' '}
                  will stop going to normal product links and will instead be redirected to the
                  recall safety notice URL if entered. If you leave this blank, scanners will see a
                  recall notice page.
                </li>
                <li>
                  <span className="font-semibold text-fg">When it takes effect:</span> Usually
                  within about 30 seconds, and never more than 60 seconds.
                </li>
                <li>
                  Normal product links remain saved so you can restore them later with{' '}
                  <span className="font-medium text-fg">Clear Recall</span>, and this action is
                  permanently recorded in the audit log.
                </li>
              </ul>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <label htmlFor="recall-reason-input" className="text-[12px] font-medium text-fg">
                  Recall Reason (required for audit log)
                </label>
                <Input
                  id="recall-reason-input"
                  value={recallReason}
                  onChange={(e) => setRecallReason(e.target.value)}
                  placeholder="Undeclared allergen batch contamination"
                />
              </div>
              <div className="space-y-1.5">
                <label
                  htmlFor="recall-override-url-input"
                  className="text-[12px] font-medium text-fg"
                >
                  Recall Safety Notice URL (optional HTTPS)
                </label>
                <Input
                  id="recall-override-url-input"
                  value={recallOverrideUrl}
                  onChange={(e) => setRecallOverrideUrl(e.target.value)}
                  placeholder="https://brand.example.com/safety-notice (optional)"
                />
                <p className="text-[11px] text-fg-secondary">
                  If you leave this blank, scanners will see a recall notice page.
                </p>
              </div>
            </div>
            {recallError !== null && (
              <p
                role="alert"
                data-testid="recall-error-message"
                className="rounded-control border border-status-error-border bg-status-error-bg p-2.5 font-medium text-status-error"
              >
                {recallError}
              </p>
            )}
            <div className="flex justify-end gap-2 border-t border-subtle pt-3.5">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={recallSubmitting}
                onClick={() => {
                  setShowRecallConfirm(false);
                  setRecallError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                variant="destructive"
                data-testid="confirm-recall-button"
                disabled={recallSubmitting}
              >
                {recallSubmitting ? 'Activating Recall...' : 'Confirm Recall Override'}
              </Button>
            </div>
          </form>
        </ModalDialog>
      )}
    </>
  );
}
